'use client'

import { useEffect, useRef, useState } from 'react'
import { HandHeart, Check, MessageCircle, Share2, Link2, X } from 'lucide-react'
import { AL_FATIHA, MAGHFIRAH_DUA } from '../../../../lib/dua-text'

/* SPRINT 3 — "Offer a dua for this masjid". A solemn, single central act:
   open the sacred text (Al-Fatiha + an optional Qur'anic maghfirah dua), confirm
   with "Aameen", record ONE dua (anon allowed, optional name, one per session),
   show the live real count, and offer a respectful testimony share at the Aameen
   moment. No candles/flames/lamps, no gamification — remembrance only. */

function sessionKey(): string {
  try {
    let k = localStorage.getItem('shaheed_session')
    if (!k) { k = Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem('shaheed_session', k) }
    return k
  } catch { return '' }
}

export default function ShaheedDua({
  slug, name, dateLine, initialCount,
}: { slug: string; name: string; dateLine: string; initialCount: number }) {
  const [open, setOpen] = useState(false)
  const [stage, setStage] = useState<'read' | 'done'>('read')
  const [count, setCount] = useState(initialCount)
  const [offeredHere, setOfferedHere] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [withName, setWithName] = useState('')
  const [copied, setCopied] = useState(false)
  const dialogRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    try { if (localStorage.getItem(`shaheed_dua_${slug}`) === '1') setOfferedHere(true) } catch {}
  }, [slug])

  // refresh the live count when the dialog opens
  useEffect(() => {
    if (!open) return
    fetch(`/api/shaheed/dua?slug=${encodeURIComponent(slug)}`).then((r) => r.json()).then((d) => { if (typeof d.count === 'number') setCount(d.count) }).catch(() => {})
  }, [open, slug])

  const offer = async () => {
    setSubmitting(true)
    try {
      const r = await fetch('/api/shaheed/dua', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug, name: withName.trim() || undefined, sessionKey: sessionKey() }),
      })
      const d = await r.json().catch(() => ({}))
      if (typeof d.count === 'number') setCount(d.count)
      try { localStorage.setItem(`shaheed_dua_${slug}`, '1') } catch {}
      setOfferedHere(true)
      setStage('done')
    } finally { setSubmitting(false) }
  }

  const shareUrl = typeof window !== 'undefined' ? `${window.location.origin}/masjids/shaheed/${slug}` : `/masjids/shaheed/${slug}`
  const shareText = `${name}${dateLine ? ` — ${dateLine}` : ''}\n${count} ${count === 1 ? 'dua has' : 'duas have'} been offered in remembrance. I offered a dua. Will you?`
  const waHref = `https://wa.me/?text=${encodeURIComponent(`${shareText}\n${shareUrl}`)}`
  const nativeShare = async () => {
    if (typeof navigator !== 'undefined' && (navigator as any).share) { try { await (navigator as any).share({ text: shareText, url: shareUrl }) } catch {} }
    else copy()
  }
  const copy = async () => { try { await navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 1800) } catch {} }

  const countLine = count > 0
    ? `${count.toLocaleString('en-IN')} ${count === 1 ? 'dua' : 'duas'} offered in remembrance of this masjid`
    : 'Be the first to offer a dua in remembrance of this masjid'

  return (
    <section className="sd" aria-label="Offer a dua">
      <p className="sd-count">{countLine}</p>
      <button type="button" className="sd-open" onClick={() => { setStage('read'); setOpen(true) }}>
        <HandHeart size={16} strokeWidth={1.6} /> {offeredHere ? 'Offer another dua' : 'Offer a dua for this masjid'}
      </button>

      {open && (
        <div className="sd-overlay" role="dialog" aria-modal="true" aria-label={`Offer a dua for ${name}`} onClick={(e) => { if (e.target === e.currentTarget) setOpen(false) }}>
          <div className="sd-dialog" ref={dialogRef}>
            <button type="button" className="sd-x" onClick={() => setOpen(false)} aria-label="Close"><X size={16} /></button>

            {stage === 'read' ? (
              <>
                <p className="sd-eyebrow">In remembrance of</p>
                <h2 className="sd-name">{name}</h2>
                {dateLine && <p className="sd-dateline">{dateLine}</p>}

                <div className="sd-text" lang="ar">
                  <h3>{AL_FATIHA.title}</h3>
                  {AL_FATIHA.ayat.map((a, i) => (
                    <div key={i} className="sd-ayah">
                      <p className="sd-ar" dir="rtl">{a.ar}</p>
                      <p className="sd-tr">{a.tr}</p>
                      <p className="sd-en">{a.en}</p>
                    </div>
                  ))}
                </div>

                <details className="sd-more">
                  <summary>A dua of forgiveness (Qur&rsquo;an 59:10)</summary>
                  <div className="sd-ayah">
                    <p className="sd-ar" dir="rtl">{MAGHFIRAH_DUA.ar}</p>
                    <p className="sd-tr">{MAGHFIRAH_DUA.tr}</p>
                    <p className="sd-en">{MAGHFIRAH_DUA.en}</p>
                  </div>
                </details>

                <label className="sd-namefield">
                  <span>Add your name (optional — leave blank to remain anonymous)</span>
                  <input value={withName} maxLength={80} onChange={(e) => setWithName(e.target.value)} placeholder="Anonymous" />
                </label>

                <button type="button" className="sd-aameen" onClick={offer} disabled={submitting}>
                  {submitting ? 'Recording…' : 'Aameen'}
                </button>
                <p className="sd-note">One dua is recorded per visit. {count > 0 ? countLine : ''}</p>
              </>
            ) : (
              <>
                <div className="sd-confirm"><Check size={22} strokeWidth={1.8} /></div>
                <h2 className="sd-name">Aameen.</h2>
                <p className="sd-thanks">Your dua has been recorded in remembrance of {name}. May Allah accept it.</p>
                <p className="sd-count big">{countLine}</p>
                <div className="sd-share">
                  <a className="sd-share-btn wa" href={waHref} target="_blank" rel="noopener noreferrer"><MessageCircle size={15} strokeWidth={1.6} /> Share on WhatsApp</a>
                  <button type="button" className="sd-share-btn" onClick={nativeShare}><Share2 size={15} strokeWidth={1.6} /> Share</button>
                  <button type="button" className="sd-share-btn" onClick={copy}>{copied ? <><Check size={15} /> Copied</> : <><Link2 size={15} /> Copy link</>}</button>
                </div>
                <button type="button" className="sd-close-quiet" onClick={() => setOpen(false)}>Close</button>
              </>
            )}
          </div>
        </div>
      )}

      <style>{`
        .sd{display:grid;gap:12px;justify-items:start}
        .sd-count{font-size:13.5px;color:#C9A84A;margin:0}
        .sd-open{display:inline-flex;align-items:center;gap:8px;background:#C9A84A;color:#1b1d22;border:0;border-radius:99px;padding:11px 20px;font-weight:700;font-size:13.5px;cursor:pointer;font-family:inherit}
        .sd-overlay{position:fixed;inset:0;z-index:1000;background:rgba(10,12,16,.78);display:grid;place-items:center;padding:20px;overflow-y:auto}
        .sd-dialog{position:relative;width:100%;max-width:540px;max-height:90vh;overflow-y:auto;background:linear-gradient(175deg,#20242C,#14161B);border:1px solid rgba(201,168,74,.3);border-radius:18px;padding:28px 26px;color:#E7E3D6}
        .sd-x{position:absolute;top:14px;right:14px;background:rgba(255,255,255,.05);border:0;color:#b8b2a2;border-radius:8px;width:30px;height:30px;display:grid;place-items:center;cursor:pointer}
        .sd-eyebrow{font-family:var(--f-mono,monospace);font-size:10px;letter-spacing:.22em;text-transform:uppercase;color:#C9A84A;margin:0 0 4px}
        .sd-name{font-family:var(--f-display,Poppins);font-size:24px;font-weight:700;color:#EFEAD9;margin:0}
        .sd-dateline{font-size:13px;color:#C9A84A;margin:6px 0 0}
        .sd-text{margin-top:20px;display:grid;gap:14px}
        .sd-text h3{font-family:var(--f-mono,monospace);font-size:11px;letter-spacing:.16em;text-transform:uppercase;color:#C9A84A;margin:0 0 4px}
        .sd-ayah{display:grid;gap:4px;padding-bottom:12px;border-bottom:1px solid rgba(201,168,74,.14)}
        .sd-ar{font-size:22px;line-height:1.9;color:#F2EEDF;margin:0;font-family:'Amiri','Scheherazade New',serif;text-align:right}
        .sd-tr{font-size:12.5px;font-style:italic;color:rgba(231,227,214,.62);margin:0}
        .sd-en{font-size:13.5px;color:rgba(231,227,214,.86);margin:0;line-height:1.55}
        .sd-more{margin-top:14px}
        .sd-more summary{cursor:pointer;font-size:12.5px;color:#C9A84A;font-weight:600}
        .sd-more .sd-ayah{margin-top:10px;border-bottom:0}
        .sd-namefield{display:grid;gap:6px;margin-top:18px}
        .sd-namefield span{font-size:11.5px;color:#b8b2a2}
        .sd-namefield input{background:rgba(255,255,255,.04);border:1px solid rgba(201,168,74,.25);border-radius:9px;padding:10px 12px;color:#E7E3D6;font:inherit}
        .sd-aameen{margin-top:16px;width:100%;background:#C9A84A;color:#1b1d22;border:0;border-radius:11px;padding:13px;font-weight:800;font-size:15px;letter-spacing:.02em;cursor:pointer;font-family:inherit}
        .sd-aameen:disabled{opacity:.6}
        .sd-note{font-size:11.5px;color:#8c8674;margin:10px 0 0;text-align:center}
        .sd-confirm{width:54px;height:54px;border-radius:54px;display:grid;place-items:center;background:rgba(201,168,74,.14);color:#C9A84A;margin:0 auto 14px}
        .sd-confirm + .sd-name{text-align:center}
        .sd-thanks{font-size:14px;color:rgba(231,227,214,.82);text-align:center;line-height:1.6;margin:10px 0 0}
        .sd-count.big{text-align:center;font-size:14px;margin-top:14px}
        .sd-share{display:flex;gap:9px;flex-wrap:wrap;justify-content:center;margin-top:20px}
        .sd-share-btn{display:inline-flex;align-items:center;gap:7px;font-size:12.5px;font-weight:600;font-family:inherit;cursor:pointer;border:1px solid rgba(201,168,74,.3);border-radius:99px;padding:9px 15px;background:rgba(255,255,255,.04);color:#E7E3D6;text-decoration:none}
        .sd-share-btn.wa{background:#25D366;border-color:#25D366;color:#fff}
        .sd-close-quiet{display:block;margin:18px auto 0;background:none;border:0;color:#8c8674;font-size:12.5px;cursor:pointer;font-family:inherit}
      `}</style>
    </section>
  )
}
